import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";

// mui components
import { Box, IconButton, Stack, Tab, Tabs } from "@mui/material";

// mui icons
import {
	AddOutlined,
	ArrowForwardOutlined,
	CloseOutlined,
} from "@mui/icons-material";

// custom components
import { TabPanel } from "../../tab-panel";
import PrimaryButton from "../../buttons/primary-button";
import Heading6 from "../../typography/heading-06";
import RequiredError from "../../required-error";
import BorrowerInformation from "../../loan-form-card/borrower-information";
import PropertyDetails from "../../loan-form-card/property-details";
import LoanOriginator from "../../loan-form-card/loan-originator";
import LoanProcessor from "../../loan-form-card/loan-processor";
import AdditionalLoanContact from "../../loan-form-card/additional-loan-contact";

// api slices
import { useCreateLoanMutation } from "../../../features/create-loan/createLoanApiSlice";
import { useFetchAEEmailsMutation } from "../../../features/fetch-ae-emails/fetchAEEmailsApiSlice";
import { useCreateAdminLoanMutation } from "../../../features/admin/create-loan/createAdminLoanApiSlice";
import { useUpdateFieldsMutation } from "../../../features/update-fields/updateFieldsApiSlice";
import { useUpdateMilestoneFreeRolesMutation } from "../../../features/update-milestone-free-roles/updateMilestoneFreeRolesApiSlice";
import { useGetOrganizationDetailsMutation } from "../../../features/organization-detail/organizationDetailApiSlice";
import { useCreateBorrowerPairMutation } from "../../../features/create-borrower-pair/createBorrowerPairApiSlice";
import { useGetBrokerListByTitleBranchMutation } from "../../../features/broker-search-title-branch/brokerSearchTitleBranchApiSlice";
import { useCreateCalyxLoanMutation } from "../../../features/create-calyx-loan/createCalyxLoanApiSlice";

// reducer slices
import { selectImpersonatedUser } from "../../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectUserDetails,
	selectSelectedOrganization,
	setLoEmail,
	setLpEmail,
} from "../../../features/auth/authSlice";
import { setBrokerListReducer } from "../../../features/broker-list/brokerListSlice";

// helper utils
import {
	requiredFields,
	requiredFieldsForAdditionalContact,
	requiredFieldsForManualLoanOriginator,
} from "../../../utils/requiredFields";
import { handleSetPropertyDetails } from "../../../utils/loan-submission/set-property-details";
import {
	selectedUserWithLoanChannel,
	selectedUser,
	isInvalidLoLpName,
	buildLoLpFullName,
	resolveBrokerFromList,
} from "../../../utils/common-helpers";
import {
	BROKER_ADMIN,
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
} from "../../../utils/common-constants";
import { borrowerCitizenshipTypeOptions } from "../../../utils/select-options";
import { runSilentValidation } from "../../../utils/silent-validation";
import { getOriginatorCompanyName } from "../../../utils/originator-company-name";
import { buildTpoFileContactFieldValues } from "../../../utils/tpo-file-contacts";
import useRegisterLoanSection from "../../../hooks/useRegisterLoanSection";
import {
	buildDirectPathFieldValue,
	withDirectPathPayload,
} from "../../../utils/direct-path.constants";
// custom modal
import LoaderModal from "../../modal/loader-modal";
import HelpTextModal from "../../modal/help-text-modal";
import CommanErrorModal from "../../modal/common-error-modal";
import { useErrorMessages } from "../../../utils/error-messages.constants";

// custom styles
import styles from "./index.module.scss";

const LoanApplicationForm = ({
	setLoanApplicationStage,
	setLoanGuid,
	loanGuid,
	xmlType,
	applicationData,
	setApplicationData,
	organizationName,
	childOrganizationName,
	currentStage,
	registerSectionHandler,
	directPath,
}) => {
	console.log("27 applicationData:", applicationData);
	console.log("27 xml type:", xmlType);

	const { getErrorMessage } = useErrorMessages();

	const [createLoan, { isLoading: creatingLoan }] = useCreateLoanMutation();
	const [fetchAEEmails, { isLoading: isFetchingAEEmails }] = useFetchAEEmailsMutation();

	const [createAdminLoan, { isLoading: creatingAdminLoan }] =
		useCreateAdminLoanMutation();

	const [createCalyxLoan, { isLoading: creatingCalyxLoan }] =
		useCreateCalyxLoanMutation();

	const [updateFields, { isLoading: isUpdatingFields }] =
		useUpdateFieldsMutation();
	
	const [updateMilestoneFreeRoles] = useUpdateMilestoneFreeRolesMutation();	

	const [createBorrowerPair, { isLoading: creatingBorrowerPair }] =
		useCreateBorrowerPairMutation();

	
	const dispatch = useDispatch();	
	const userDetails = useSelector(selectUserDetails);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const impersonatedUser = useSelector(selectImpersonatedUser);

	const [loanApplicationData, setLoanApplicationData] = useState([]);
	const firstTimeHomebuyerSelections = useRef(new Map());

	const setFirstTimeHomebuyerSelection = useCallback((borrowerIndex, value) => {
		firstTimeHomebuyerSelections.current.set(borrowerIndex, value);
	}, []);

	const [foreignNationalIndex, setForeignNationalIndex] = useState([]);

	const [tabValue, setTabValue] = useState(0);

	const [propertyInfo, setPropertyInfo] = useState({});
	const [grossRent, setGrossRent] = useState("");

	const [homeownersInsurance, setHomeownersInsurance] = useState("");
	const [floodInsurance, setFloodInsurance] = useState("");
	const [propertyTaxes, setPropertyTaxes] = useState("");
	const [realEstateTaxes, setRealEstateTaxes] = useState("");
	const [hoaDues, setHoaDues] = useState("");

	const [getBrokerListByTitleBranch] = useGetBrokerListByTitleBranchMutation();
	const [getOrganizationDetails] = useGetOrganizationDetailsMutation();

	const [currentOrgName, setCurrentOrgName] = useState({});
	const [currentOrgDetails, setCurrentOrgDetails] = useState({});

	const [organizationDetails, setOrganizationDetails] = useState({});
	const [childOrganizationDetails, setChildOrganizationDetails] = useState({});

	const [organizationLicense, setOrganizationLicense] = useState("");

	const [loanOriginatorEmail, setLoanOriginatorEmail] = useState("");
	const [loanOriginatorId, setLoanOriginatorId] = useState("");
	const [organizationUsers, setOrganizationUsers] = useState([]);
	const [loanOriginator, setLoanOriginator] = useState({});

	const [loanProcessorEmail, setLoanProcessorEmail] = useState("");
	const [loanProcessorId, setLoanProcessorId] = useState("");
	const [organizationProcessor, setOrganizationProcessor] = useState([]);
	const [loanProcessor, setLoanProcessor] = useState({});

	const [additionalContact, setAdditionalContact] = useState({
		name: "",
		phone: "",
		email: "",
	});

	const [manualOriginator, setManualOriginator] = useState(false);
	const [originatorData, setOriginatorData] = useState({
		orgName: "",
		address: "",
		unitNumber: "",
		city: "",
		state: "",
		zipcode: "",
		orgNmls: "",
		orgStateLicense: "",
		originatorEmail: "",
		originatorFirstName: "",
		originatorLastName: "",
		originatorNmls: "",
		originatorStateLicense: "",
		originatorPhone: "",
	});

	const [helpText, setHelpText] = useState("");
	const [extraHelpText, setExtraHelpText] = useState("");
	const [helpTextOpen, setHelpTextOpen] = useState(false);

	const handleOpenHelpText = () => {
		setHelpTextOpen(true);
	};

	const handleCloseHelpText = () => {
		setHelpTextOpen(false);
	};

	const isAdmin = userDetails?.user_roles?.includes("admin") === true;
	console.log("260 is admin:", isAdmin);

	const isInternalUser =
		userDetails?.user_roles?.includes("executive") ||
		userDetails?.user_roles?.includes("manager") ||
		userDetails?.user_roles?.includes("AE") ||
		userDetails?.user_roles?.includes("AM") ||
		userDetails?.user_roles?.includes("PA") ||
		userDetails?.user_roles?.includes("assistant");

	// For multi-org users, use the explicitly selected org; fall back to organizationName for single-org users
	const effectiveOrgName = selectedOrganization || organizationName;

	let brokerSearchOrgName;
	let includeChildren;

	if (
		selectedUser(userDetails, impersonatedUser)?.access_level ===
		TPO_SECONDARY ||
		selectedUser(userDetails, impersonatedUser)?.access_level === BROKER_ADMIN
	) {
		// brokerSearchOrgName = encodeURIComponent(effectiveOrgName);
		brokerSearchOrgName = effectiveOrgName;
		includeChildren = true;
	} else if (
		selectedUser(userDetails, impersonatedUser)?.child_org_name !== "" && !selectedOrganization
	) {
		// brokerSearchOrgName = encodeURIComponent(childOrganizationName);
		brokerSearchOrgName = childOrganizationName
		includeChildren = false;
	} else {
		// brokerSearchOrgName = encodeURIComponent(effectiveOrgName);
		brokerSearchOrgName = effectiveOrgName
		includeChildren = false;
	}

	const regexNdcOrCorr = /\b(?:NDC|CORR)\b/i;
	const isOrgNdcOrCorr = regexNdcOrCorr.test(brokerSearchOrgName);

	console.log("is organization NDC or CORR:", isOrgNdcOrCorr);

	const allowManualInput =
		isAdmin ||
		(isOrgNdcOrCorr &&
			(isInternalUser ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
				LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails)?.loan_channel ===
				LOAN_CHANNEL_CONSTANTS.CORR));

	useEffect(() => {
		const getBrokerListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Officer",
				include_child: includeChildren,
			}).unwrap();

			dispatch(setBrokerListReducer(searchResults));

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan originator updated:", updatedArr);
			setOrganizationUsers(updatedArr);
		};

		const getProcessorListFunc = async () => {
			let searchResults = await getBrokerListByTitleBranch({
				orgName: brokerSearchOrgName,
				title: "Loan Processor",
				include_child: includeChildren,
			}).unwrap();

			let updatedArr = searchResults.map((item) => ({
				...item,
				label: item?.user?.email || "",
				value: item?.user?.email || "",
			}));

			console.log("150 loan processor updated:", updatedArr);
			setOrganizationProcessor(updatedArr);
		};

		const getOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(effectiveOrgName),
			}).unwrap();
			setOrganizationDetails(searchResults);
		};

		const getChildOrganizationDetailsFunc = async () => {
			const searchResults = await getOrganizationDetails({
				organizationName: encodeURIComponent(childOrganizationName),
			}).unwrap();

			setChildOrganizationDetails(searchResults);
		};

		if (
			effectiveOrgName !== null &&
			effectiveOrgName !== undefined &&
			effectiveOrgName !== ""
		) {
			getBrokerListFunc();
			getProcessorListFunc();
			getOrganizationDetailsFunc();

			// Only fetch child organization details if no organization is explicitly selected
			// and the user has a default child organization
			if (
				!selectedOrganization &&
				selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
			) {
				getChildOrganizationDetailsFunc();
			}
		}
	}, [effectiveOrgName, selectedOrganization]);

	useEffect(() => {
		// Simple priority: selectedOrganization > childOrganizationName > organizationName
		const priorityOrgName = selectedOrganization || childOrganizationName || organizationName;
		setCurrentOrgName(priorityOrgName);
		setCurrentOrgDetails(organizationDetails);
	}, [
		organizationName,
		organizationDetails,
		childOrganizationName,
		selectedOrganization,
	]);

	useEffect(() => {
		if (!applicationData) return;

		const payloadTpo =
			applicationData?.tPO || applicationData?.loan_data?.tpo || applicationData?.tpo || {};

		const initialLoanOriginatorEmail =
			payloadTpo?.lOEmail || payloadTpo?.loEmail || payloadTpo?.lOEmail || "";
		const initialLoanProcessorEmail =
			payloadTpo?.lPEmail || payloadTpo?.lpEmail || payloadTpo?.lPEmail || "";
		const initialLoanOriginatorId =
			payloadTpo?.lOID || payloadTpo?.lOid || payloadTpo?.lOID || "";
		const initialLoanProcessorId =
			payloadTpo?.lPID || payloadTpo?.lPid || payloadTpo?.lPID || "";

		if (!loanOriginatorEmail && initialLoanOriginatorEmail) {
			setLoanOriginatorEmail(initialLoanOriginatorEmail);
		}
		if (!loanOriginatorId && initialLoanOriginatorId) {
			setLoanOriginatorId(String(initialLoanOriginatorId));
		}
		if (!loanProcessorEmail && initialLoanProcessorEmail) {
			setLoanProcessorEmail(initialLoanProcessorEmail);
		}
		if (!loanProcessorId && initialLoanProcessorId) {
			setLoanProcessorId(String(initialLoanProcessorId));
		}
	}, [
		applicationData,
		loanOriginatorEmail,
		loanOriginatorId,
		loanProcessorEmail,
		loanProcessorId,
	]);

	useEffect(() => {
		const shouldResolveOriginator = Boolean(
			loanOriginatorId || loanOriginatorEmail
		);

		if (shouldResolveOriginator && organizationUsers?.length) {
			const originatorDetails = resolveBrokerFromList(
				organizationUsers,
				loanOriginatorId,
				loanOriginatorEmail
			);

			// Only overwrite when a match is found so a failed lookup (async list
			// load, id type mismatch) cannot clobber a valid loanOriginator object.
			if (originatorDetails) {
				setLoanOriginator(originatorDetails);
			}
		}
	}, [loanOriginatorEmail, loanOriginatorId, organizationUsers]);

	useEffect(() => {
		const shouldResolveProcessor = Boolean(
			loanProcessorId || loanProcessorEmail
		);

		if (shouldResolveProcessor && organizationProcessor?.length) {
			const processorDetails = resolveBrokerFromList(
				organizationProcessor,
				loanProcessorId,
				loanProcessorEmail
			);
			if (processorDetails) {
				setLoanProcessor(processorDetails);
			}
		}
	}, [loanProcessorEmail, loanProcessorId, organizationProcessor]);

	useEffect(() => {
		const sortBorrowerPair = async () => {
			console.log("115 data:", applicationData);

			let pairArr = [];

			applicationData?.applications?.map((item) => {
				pairArr.push(item.borrower);
				pairArr.push(item.coBorrower || item.coborrower);
				return;
			});

			console.log("120 pair arr:", pairArr);

			setLoanApplicationData(pairArr);
			setPropertyInfo(applicationData?.property);
		};

		if (applicationData !== undefined && loanApplicationData?.length === 0) {
			sortBorrowerPair();
		}
	}, [applicationData]);

	const [error, setError] = useState("");
	const [aeEmailErrors, setAeEmailErrors] = useState(null);
	const [commonErrorData, setCommonErrorData] = useState(null);
	const [commonErrorModalVisible, setCommonErrorModalVisible] = useState(false);

	const handleOpenCommonErrorModal = (errorKey) => {
		setCommonErrorData(getErrorMessage(errorKey));
		setCommonErrorModalVisible(true);
	};

	const handleCloseCommonErrorModal = () => {
		setCommonErrorModalVisible(false);
		setCommonErrorData(null);
	};

	useEffect(() => {
		console.log("275 application data:", applicationData);

		if (applicationData !== undefined) {
			console.log(
				"275 property type:",
				applicationData?.applications[0]?.propertyUsageType
			);

			setGrossRent(
				applicationData?.applications[0]?.propertyUsageType === "Investor"
					? applicationData?.subjectPropertyGrossRentalIncomeAmount
					: 0
			);

			handleSetPropertyDetails(
				applicationData,
				setHomeownersInsurance,
				setFloodInsurance,
				setPropertyTaxes,
				setRealEstateTaxes,
				setHoaDues
			);
		}
	}, [applicationData]);

	const updateField = async (
		loanGuid,
		apiObj,
		shouldAdvance = true,
		aeEmailData,
		loanNumber
	) => {
		console.log("310 api obj:", apiObj);

		let salesRepName =
			manualOriginator &&
				(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
					selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				? organizationDetails?.sales_rep_name
				: loanOriginator?.organization?.sales_rep_name;

		let valuesArr = [
			{ id: "TPO.X3", value: DateTime.now().toFormat("MM-dd-yyyy") },
			{
				id: "CX.PROPERTY.COMMERCIAL.UNITS",
				value: apiObj?.property?.financedNumberOfUnits || "",
			},
			{ id: "NEWHUD.X223", value: "" },
			{ id: "NEWHUD.X224", value: "" },
			{ id: "389", value: "" },
			{ id: "1620", value: "" },
			{ id: 315, value: getOriginatorCompanyName(apiObj, loanOriginator?.organization) },
			{ id: 313, value: loanOriginator?.organization?.city },
			{ id: 321, value: loanOriginator?.organization?.state },
			{ id: 323, value: loanOriginator?.organization?.zip },
			{ id: 3237, value: loanOriginator?.organization?.nmls_id },
			{ id: 1256, value: salesRepName },
			{ id: "CX.LC.AE.EMAIL", value: aeEmailData?.loan_processor_ae_email || aeEmailData?.branch_ae_email || aeEmailData?.company_ae_email || "" },
			{ id: "CX.LC.AE.EMAIL.COMPANY", value: aeEmailData?.company_ae_email || "" },
			{ id: "CX.LC.AE.EMAIL.LO", value: aeEmailData?.loan_processor_ae_email || "" },
			{ id: "TPO.X62", value:  loanOriginatorId},
			{ id: "TPO.X75", value:  loanProcessorId},
		];

		if (selectedUser(userDetails, impersonatedUser)?.child_org_name !== "") {
			valuesArr.push({
				id: "CX.LC.AE.EMAIL.BRANCH",
				value: aeEmailData?.branch_ae_email || "",
			});
		}

		const firstTimeHomebuyerFields = Array.from(
			firstTimeHomebuyerSelections.current.entries()
		).reduce((fieldValues, [borrowerIndex, selection]) => {
			if (!selection) {
				return fieldValues;
			}

			const id = borrowerIndex % 2 === 0 ? "4973" : "4974";
			const fieldValue = { id, value: selection === "Yes", lock: true };
			const existingFieldIndex = fieldValues.findIndex(
				(field) => field.id === id
			);

			if (existingFieldIndex >= 0) {
				fieldValues[existingFieldIndex] = fieldValue;
			} else {
				fieldValues.push(fieldValue);
			}

			return fieldValues;
		}, []);
		valuesArr.push(...firstTimeHomebuyerFields);

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			valuesArr.push({
				id: "CX.LOLP.SELECT.MANUAL.METHOD",
				value: manualOriginator ? "Y" : "N",
			});
			if (manualOriginator) {
				valuesArr.push({
					id: "315",
					value: originatorData?.orgName,
				});
				valuesArr.push({
					id: "313",
					value: originatorData?.city,
				});
				valuesArr.push({
					id: "321",
					value: originatorData?.state,
				});
				valuesArr.push({
					id: "323",
					value: originatorData?.zipcode,
				});
				valuesArr.push({
					id: "3237",
					value: originatorData?.orgNmls,
				});
			}
		}

		if (
			apiObj?.uldd?.totalMortgagedPropertiesCount === undefined ||
			apiObj?.uldd?.totalMortgagedPropertiesCount === "" ||
			apiObj?.uldd?.totalMortgagedPropertiesCount < 1 ||
			apiObj?.uldd?.totalMortgagedPropertiesCount > 99
		) {
			valuesArr.push({ id: "ULDD.TotalMortgagedPropertiesCount", value: "1" });
		}

		console.log("300 foreign national:", foreignNationalIndex);

        /*** Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration START ***/

		// Initialize hasForeignNational as "No"
		var hasForeignNational = "No";

		// Loop through the range of indices (0 to 5) for 3 borrower pairs
		for (let index = 0; index < 6; index++) {
			// Create the id based on the index
			const id = `CX.FOREIGNNATLBORR${index + 1}`;

			// Set value to "Yes" if index is in foreignNationalIndex, otherwise "No"
			const value = foreignNationalIndex.includes(index) ? "Yes" : "No";

			// If any borrower is "Yes", update hasForeignNational
			if (value === "Yes") {
				hasForeignNational = "Yes";
			}

			// Push borrower-specific value
			valuesArr.push({ id, value });
		}

		// Push overall CX.FOREIGNNATL value
		valuesArr.push({
			id: "CX.FOREIGNNATL",
			value: hasForeignNational
		});

		const borrower1Citizenship = loanApplicationData[0]?.urla2020CitizenshipResidencyType;
		console.log("Borrower Details: " , borrower1Citizenship);
		const firstBorrowerCitizenShipType = borrowerCitizenshipTypeOptions.find(option => option.value === borrower1Citizenship);
		console.log("Borrower aa: " , firstBorrowerCitizenShipType);

		const foreignNotionalFirstBorrower = foreignNationalIndex.includes(0) ? "Yes" : "No";
		valuesArr.push({
			id: "CX.CITIZENSHIP",
			value: foreignNotionalFirstBorrower === "Yes" ? "Foreign National" : firstBorrowerCitizenShipType?.label
		});

        /***  Foreign National Citizenship parameter CX.CITIZENSHIP CX.FOREIGNNATL configuration END ***/		


		if (loanOriginatorEmail !== undefined && loanOriginatorEmail !== "") {
			valuesArr.push({
				id: "TPO.X67",
				value: loanOriginator?.cell_phone,
			});

			console.log("600 loanOriginator:", loanOriginator);

			valuesArr.push({
				id: "2306",
				value: loanOriginator?.state_license,
			});
		}
		// Added blank values for below fields - LC 206
		valuesArr.push({
			id: "URLA.X189",
			value: "",
		});
		valuesArr.push({
			id: "URLA.X190",
			value: "",
		});

		const directPathFieldValue = buildDirectPathFieldValue(directPath);
		if (directPathFieldValue) {
			valuesArr.push(directPathFieldValue);
		}

		console.log("340 loan update values:", valuesArr);

		const updateFieldValues = await updateFields({
			loan_guid: loanGuid,
			field_values: valuesArr,
			selectedOrganization: effectiveOrgName,
			userDetails,
		}).unwrap();
		console.log("430 file res:", updateFieldValues);

		if (updateFieldValues?.response === "Field values updated successfully.") {
			try {
				await updateMilestoneFreeRoles({
					loan_guid: loanGuid,
					loan_officer: {
						entityId: loanOriginatorId,
						email: loanOriginator?.user?.email || loanOriginatorEmail,
					},
					loan_processor: {
						entityId: loanProcessorId,
						email: loanProcessor?.user?.email || loanProcessorEmail,
					},
					account_executive: {
						entityId:
							aeEmailData?.identifiers?.loan_processor_sales_rep_entity_id ||
							loanOriginator?.organization?.sales_rep_id,
						email:
							aeEmailData?.loan_processor_ae_email ||
							aeEmailData?.ae_email ||
							"",
					},
				}).unwrap();
			} catch (err) {
				console.error("[Milestone Free Roles] Sync failed:", err);
			}			

			try {
				const isManualNdcOrCorr =
					manualOriginator &&
					(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR);

				const tpoFileContactValues = buildTpoFileContactFieldValues({
					aeEmailData,
					aeEntityId:
						aeEmailData?.identifiers?.loan_processor_sales_rep_entity_id ||
						loanOriginator?.organization?.sales_rep_id,
					organizationId: isManualNdcOrCorr
						? organizationDetails?.organization_id
						: loanOriginator?.organization?.organization_id,
				});

				if (tpoFileContactValues) {
					await updateFields({
						loan_guid: loanGuid,
						field_values: tpoFileContactValues,
						selectedOrganization: effectiveOrgName,
						userDetails,
					}).unwrap();
				}
			} catch (err) {
				console.error("[TPO File Contacts] Sync failed:", err);
			}

			setError("");
			if (shouldAdvance) {
				window.scrollTo({
					top: 0,
					left: 0,
					behavior: "smooth",
				});
				setLoanApplicationStage(currentStage + 1);
			}
			return true;
		}

		setError(updateFieldValues?.response);
		return false;
	};

	const reorderResidencesArray = (arr) => {
		let newArr = [];
		let id = 0;

		for (let i = 0; i < arr.length; i++) {
			newArr.push({ ...arr[i], id: `Residence/${id}` });
			id++;
		}

		return newArr;
	};

	const buildLoLpFullName = (broker) => {
		if (!broker) return "";
		const firstName = broker?.user?.first_name || broker?.first_name || "";
		const lastName = broker?.user?.last_name || broker?.last_name || "";
		return [firstName, lastName].filter(Boolean).join(" ").trim();
	};

	const resolveBrokerFromList = (brokerList = [], brokerId, brokerEmail) => {
		if (!brokerList?.length) return null;
		const byId = brokerList.find((item) => item?.broker_id === brokerId);
		if (byId) return byId;
		return brokerList.find((item) => item?.user?.email === brokerEmail) || null;
	};

	const isInvalidLoLpName = (name = "") => {
		return !name || name.trim().toLowerCase() === "undefined undefined";
	};

	const ensureValidLoLpForSubmit = async () => {
		const isNdcOrCorrChannel =
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR;

		let effectiveLoanOriginator = loanOriginator;
		let effectiveLoanProcessor = loanProcessor;

		if (
			!isNdcOrCorrChannel &&
			isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))
		) {
			effectiveLoanOriginator =
				resolveBrokerFromList(
					organizationUsers,
					loanOriginatorId,
					loanOriginatorEmail
				) || effectiveLoanOriginator;

			if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) {
				try {
					const refreshedOriginators = await getBrokerListByTitleBranch({
						orgName: brokerSearchOrgName,
						title: "Loan Officer",
						include_child: includeChildren,
					}).unwrap();

					console.log("resolveBrokerFromList LO (refresh) - inputs:", resolveBrokerFromList(
							refreshedOriginators,
							loanOriginatorId,
							loanOriginatorEmail
						));
					console.log("resolveBrokerFromList  LO (refresh) - result:", effectiveLoanOriginator);

					effectiveLoanOriginator =
						resolveBrokerFromList(
							refreshedOriginators,
							loanOriginatorId,
							loanOriginatorEmail
						) || effectiveLoanOriginator;
				} catch (err) {
					console.error("LO details refresh before submit failed:", err);
				}
			}

			if (!isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) {
				setLoanOriginator(effectiveLoanOriginator);
			}
		}
		

		if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
			effectiveLoanProcessor =
				resolveBrokerFromList(
					organizationProcessor,
					loanProcessorId,
					loanProcessorEmail
				) || effectiveLoanProcessor;	

			if (isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
				try {
					const refreshedProcessors = await getBrokerListByTitleBranch({
						orgName: brokerSearchOrgName,
						title: "Loan Processor",
						include_child: includeChildren,
					}).unwrap();

					effectiveLoanProcessor =
						resolveBrokerFromList(
							refreshedProcessors,
							loanProcessorId,
							loanProcessorEmail
						) || effectiveLoanProcessor;
						
				} catch (err) {
					console.error("LP details refresh before submit failed:", err);
				}
			}

			if (!isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))) {
				setLoanProcessor(effectiveLoanProcessor);
			}
		}

		if (
			(!isNdcOrCorrChannel &&
				isInvalidLoLpName(buildLoLpFullName(effectiveLoanOriginator))) ||
			isInvalidLoLpName(buildLoLpFullName(effectiveLoanProcessor))
		) {
			console.error(
				"Blocking submission: invalid LO/LP name for TPO.X61/TPO.X74",
				{
					loName: buildLoLpFullName(effectiveLoanOriginator),
					lpName: buildLoLpFullName(effectiveLoanProcessor),
				}
			);
			setError(
				"Loan Officer / Loan Processor details could not be verified. Please reselect the Loan Originator and Loan Processor, then try again."
			);
			return null;
		}

		return { effectiveLoanOriginator, effectiveLoanProcessor };
	};

	const handleNext = async (shouldAdvance = true, skipValidation = false) => {
		setError("");
		await ensureValidLoLpForSubmit();

		console.log("450 propertyInfo:", propertyInfo);

		if (
			!skipValidation &&
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError,
				firstTimeHomebuyerSelections.current
			)
		) {
			return false;
		}

		if (
			!skipValidation &&
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return false;
			}
		}

		if (
			!skipValidation &&
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return false;
			}
		}

		const resolvedLoLp = await ensureValidLoLpForSubmit();

		if (!resolvedLoLp) {
			return false;
		}

		const { effectiveLoanOriginator, effectiveLoanProcessor } = resolvedLoLp;

		let borrowerPairData = [];

		for (let index = 0; index < loanApplicationData.length; index++) {
			if (index % 2 === 0) {
				if (
					loanApplicationData[index]?.urla2020CitizenshipResidencyType ===
					"Foreign National"
				) {
					loanApplicationData[index].urla2020CitizenshipResidencyType =
						"NonPermanentResidentAlien";
				}

				if (
					loanApplicationData[index + 1]?.urla2020CitizenshipResidencyType ===
					"Foreign National"
				) {
					loanApplicationData[index + 1].urla2020CitizenshipResidencyType =
						"NonPermanentResidentAlien";
				}

				borrowerPairData.push([
					{
						borrower: loanApplicationData[index],
						coborrower: loanApplicationData[index + 1],
					},
				]);
			}
		}

		console.log("285 borrower pair data;", borrowerPairData);

		let residenceArr = [];

		console.log("625 applicationData:", applicationData?.applications);

		for (
			let index = 0;
			index < applicationData?.applications?.length;
			index++
		) {
			console.log("500 data:", applicationData?.applications[index]);
			console.log(
				"500 res data:",
				applicationData?.applications[index]?.residences
			);

			residenceArr.push(
				reorderResidencesArray(applicationData?.applications[index]?.residences)
			);

			console.log("550 residence arr:", residenceArr);
		}

		let applications = [];

		console.log("585 loan app data:", loanApplicationData);

		for (let index = 0; index < loanApplicationData.length; index += 2) {
			console.log("585 index:", index);

			applications.push([
				{
					borrower: {
						...loanApplicationData[index],
						mailingAddressSameAsPresentIndicator: false,
					},
					coBorrower: {
						...loanApplicationData[index + 1],
						mailingAddressSameAsPresentIndicator: false,
					},
					assets: applicationData?.applications[index]?.assets,
					otherAssets: applicationData?.applications[index]?.otherAssets,
					liabilities: applicationData?.applications[index]?.liabilities,
					otherLiabilities:
						applicationData?.applications[index]?.otherLiabilities,
					employment: applicationData?.applications[index]?.employment,
					otherIncomeSources:
						applicationData?.applications[index]?.otherIncomeSources,
					propertyUsageType:
						applicationData?.applications[index]?.propertyUsageType,
					reoProperties: applicationData?.applications[index]?.reoProperties,
					residences: residenceArr[index === 0 ? 0 : index / 2],
				},
			]);
		}

		console.log("450 applications:", applications);

		// Update the monthly payment for the specified fee type
		let updatedFees = [];

		if (
			applicationData?.fees === undefined ||
			applicationData?.fees?.length === 0
		) {
			updatedFees.push({
				feeType: "FloodInsurance",
				monthlyPayment: floodInsurance,
			});

			updatedFees.push({
				feeType: "PropertyTaxes",
				monthlyPayment: propertyTaxes,
			});
		} else {
			applicationData?.fees?.map((fee) => {
				if (fee.feeType === "FloodInsurance") {
					updatedFees.push({ ...fee, monthlyPayment: floodInsurance });
				}

				if (fee.feeType === "PropertyTaxes") {
					updatedFees.push({ ...fee, monthlyPayment: propertyTaxes });
				}

				return fee;
			});
		}

		console.log("600 updated fees:", updatedFees);

		setApplicationData({
			...applicationData,
			subjectPropertyGrossRentalIncomeAmount: grossRent,
			proposedHazardInsuranceAmount: homeownersInsurance,
			proposedDuesAmount: hoaDues,
			fees: updatedFees,
			proposedRealEstateTaxesAmount:
				updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
					?.monthlyPayment || "0",
			applications,
			property: propertyInfo,
			tPO: {
				lOEmail: loanOriginatorEmail,
				lPEmail: loanProcessorEmail,
			},
		});

		let tpoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else if (
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["companyName"] = organizationDetails?.name;
			tpoData["companyId"] = organizationDetails?.company_id;
			tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
			tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
			tpoData["companyAddress"] = organizationDetails?.address;
			tpoData["companyCity"] = organizationDetails?.city;
			tpoData["companyState"] = organizationDetails?.state;
			tpoData["companyZip"] = organizationDetails?.zip;
			tpoData["companyPhone"] = organizationDetails?.phone;
			tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
			tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
		} else {
			tpoData["companyName"] = effectiveLoanOriginator?.organization?.name;
			tpoData["companyId"] = effectiveLoanOriginator?.organization?.company_id;
			tpoData["companyOrganizationId"] =
				effectiveLoanOriginator?.organization?.organization_id;
			tpoData["companyLegalName"] =
				effectiveLoanOriginator?.organization?.company_legal_name;
			tpoData["companyAddress"] = effectiveLoanOriginator?.organization?.address;
			tpoData["companyCity"] = effectiveLoanOriginator?.organization?.city;
			tpoData["companyState"] = effectiveLoanOriginator?.organization?.state;
			tpoData["companyZip"] = effectiveLoanOriginator?.organization?.zip;
			tpoData["companyPhone"] = effectiveLoanOriginator?.organization?.phone;
			tpoData["companyAeName"] = effectiveLoanOriginator?.organization?.sales_rep_name;
			tpoData["companyAeUserName"] = effectiveLoanOriginator?.organization?.sales_rep_id;
		}

		if (
			childOrganizationDetails ||
			selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
		) {
			tpoData["branchName"] = childOrganizationDetails?.name;
			tpoData["branchId"] = childOrganizationDetails?.company_id;
			tpoData["branchOrganizationId"] =
				childOrganizationDetails?.organization_id;
			tpoData["branchLegalName"] = childOrganizationDetails?.company_legal_name;
			tpoData["branchAddress"] = childOrganizationDetails?.address;
			tpoData["branchCity"] = childOrganizationDetails?.city;
			tpoData["branchState"] = childOrganizationDetails?.state;
			tpoData["branchZip"] = childOrganizationDetails?.zip;
			tpoData["branchPhone"] = childOrganizationDetails?.phone;
			tpoData["branchAeName"] = childOrganizationDetails?.sales_rep_name;
			tpoData["branchAeUserName"] = childOrganizationDetails?.sales_rep_id;
		}

		if (
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
		) {
			tpoData["loEmail"] = additionalContact?.email;
			tpoData["loName"] = additionalContact?.name;
			tpoData["loBusinessPhone"] = additionalContact?.phone;
		} else {
			tpoData["loEmail"] = effectiveLoanOriginator?.user?.email;
			tpoData["loStatus"] = effectiveLoanOriginator?.login_enabled;
			tpoData["loAddress"] = effectiveLoanOriginator?.address;
			tpoData["loCity"] = effectiveLoanOriginator?.city;
			tpoData["loState"] = effectiveLoanOriginator?.state;
			tpoData["loZip"] = effectiveLoanOriginator?.zip_code;
			tpoData["loName"] = buildLoLpFullName(effectiveLoanOriginator);
			tpoData["loBusinessPhone"] = effectiveLoanOriginator?.phone;
		}

		console.log("775 loanProcessor email:", loanProcessorEmail);
		console.log("775 loanProcessor:", effectiveLoanProcessor);

		tpoData["lpEmail"] = effectiveLoanProcessor?.user?.email;
		tpoData["lpStatus"] = effectiveLoanProcessor?.login_enabled;
		tpoData["lpAddress"] = effectiveLoanProcessor?.address;
		tpoData["lpCity"] = effectiveLoanProcessor?.city;
		tpoData["lpState"] = effectiveLoanProcessor?.state;
		tpoData["lpZip"] = effectiveLoanProcessor?.zip_code;
		tpoData["lpName"] = buildLoLpFullName(effectiveLoanProcessor);
		tpoData["lpBusinessPhone"] = effectiveLoanProcessor?.phone;

		console.log("650 tpoData:", tpoData);

		let originatorInfoData = {};

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			originatorInfoData["originatorAddressLineText"] = originatorData?.address;
			originatorInfoData["originatorAddressUnitIdentifier"] =
				originatorData?.unitNumber;
			originatorInfoData["interviewersCompanyStateLicense"] =
				originatorData?.orgStateLicense;
			originatorInfoData["interviewerEmail"] = originatorData?.originatorEmail;
			originatorInfoData["originatorFirstName"] =
				originatorData?.originatorFirstName;
			originatorInfoData["originatorLastName"] =
				originatorData?.originatorLastName;
			originatorInfoData["nmlsLoanOriginatorId"] =
				originatorData?.originatorNmls;
			originatorInfoData["interviewerLicenseIdentifier"] =
				originatorData?.originatorStateLicense;
			originatorInfoData["interviewerPhoneNumber"] =
				originatorData?.originatorPhone;
		} else {
			originatorInfoData["originatorAddressLineText"] =
				effectiveLoanOriginator?.organization?.address;
			originatorInfoData["interviewerEmail"] =
				effectiveLoanOriginator?.user?.email;
			originatorInfoData["interviewerName"] = buildLoLpFullName(
				effectiveLoanOriginator
			);
			originatorInfoData["originatorFirstName"] =
				effectiveLoanOriginator?.user?.first_name;
			originatorInfoData["originatorLastName"] =
				effectiveLoanOriginator?.user?.last_name;
			originatorInfoData["nmlsLoanOriginatorId"] =
				effectiveLoanOriginator?.nmls_id;

			originatorInfoData["interviewerPhoneNumber"] =
				effectiveLoanOriginator?.phone;
			originatorInfoData["interviewerLicenseIdentifier"] = "";
		}

		console.log("690 originatorInfoData:", originatorInfoData);

		let apiObj = {
			user_email: loanOriginatorEmail,
			loan_data: {
				...applicationData,
				subjectPropertyGrossRentalIncomeAmount: grossRent,
				proposedHazardInsuranceAmount: homeownersInsurance,
				proposedDuesAmount: hoaDues,
				fees: updatedFees,
				proposedRealEstateTaxesAmount:
					updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
						?.monthlyPayment || "0",
				applications: applications[0],
				property: propertyInfo,
				tpo: {
					loAeName: effectiveLoanOriginator?.organization?.sales_rep_id,
					...tpoData,
				},
				...originatorInfoData,
			},
		};

		console.log("265 upload supporting docs:", apiObj);

		// Step A: Fetch AE Emails — must succeed before loan creation
		let aeEmailData = null;
		try {
			setAeEmailErrors(null);
			const aeResult = await fetchAEEmails({
				broker_id: userDetails?.organizations?.find(org => org.name === selectedOrganization)?.tpo_broker_id || userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id,
				loan_processor_broker_id: loanOriginatorId,
			}).unwrap();
			const hasAEEmail =
				aeResult?.ae_email ||
				aeResult?.branch_ae_email ||
				aeResult?.company_ae_email;
			if (!hasAEEmail) {
				handleOpenCommonErrorModal("AE_EMAIL_NOT_FOUND");
				return;
			}
			if (aeResult?.errors) {
				setAeEmailErrors(aeResult.errors);
				console.warn("[AE Email] Partial fetch errors:", aeResult.errors);
			}
			aeEmailData = aeResult;
		} catch (err) {
			console.error("[AE Email] Fetch failed:", err);
			setError(err?.data?.message || "AE email lookup failed. Please try again.");
			return;
		}

		// Step B: Create Loan
		try {
			if (loanGuid) {
				return updateField(loanGuid, apiObj?.loan_data, shouldAdvance);
			}

			let fileUpload;

			const newApplicationNumber = (loanApplicationData[0]?.lastName + '-' + (loanApplicationData[0]?.mailingAddress?.urla2020StreetAddress ?? 'Unknown Address'));
			const oldApplicationNumber = (applicationData?.applications[0]?.borrower?.lastName + '-' + (applicationData?.applications[0]?.borrower?.mailingAddress?.urla2020StreetAddress ?? 'Unknown Address'));
			const hasApplicationNumberChanged = newApplicationNumber !== oldApplicationNumber;

			if (isAdmin) {
				fileUpload = await createAdminLoan(
					withDirectPathPayload(
						{
							...apiObj,
							loan_channel: impersonatedUser?.loan_channel,
							tpo_user_id: userDetails?.user_tpo_id,
							application_number: newApplicationNumber,
							...(hasApplicationNumberChanged && {
								old_application_number: oldApplicationNumber,
							}),
						},
						directPath
					)
				).unwrap();
			} else {
				fileUpload = await createLoan(
					withDirectPathPayload(
						{
							...apiObj?.loan_data,
							tpo_user_id: userDetails?.user_tpo_id,
							application_number: newApplicationNumber,
							...(hasApplicationNumberChanged && {
								old_application_number: oldApplicationNumber,
							}),
							selectedOrganization,
							userDetails,
						},
						directPath
					)
				).unwrap();
			}

			console.log("90 create impersonated loan:", fileUpload);
			console.log("385 loan originator email:", loanOriginatorEmail);
			dispatch(setLoEmail(loanOriginatorEmail));
			setLoanGuid(fileUpload?.GUID);

			// create borrower pair
			if (loanApplicationData?.length >= 3) {
				let borrowerPair;
				for (let index = 1; index < borrowerPairData.length; index++) {
					borrowerPair = await createBorrowerPair({
						loanGuid: fileUpload?.GUID,
						data: {
							...borrowerPairData[index][0],
							assets: applicationData?.applications[index]?.assets,
							employment: applicationData?.applications[index]?.employment,
							liabilities: applicationData?.applications[index]?.liabilities,
							otherIncomeSources:
								applicationData?.applications[index]?.otherIncomeSources,
							otherLiabilities:
								applicationData?.applications[index]?.otherLiabilities,
							propertyUsageType:
								applicationData?.applications[index]?.propertyUsageType,
							reoProperties:
								applicationData?.applications[index]?.reoProperties,
							residences: residenceArr[index],
						},
						selectedOrganization: effectiveOrgName,
						userDetails,
					}).unwrap();
				}

				console.log("750 borrower pair data:", borrowerPair);
			}

			return updateField(
				fileUpload?.GUID,
				apiObj?.loan_data,
				shouldAdvance,
				aeEmailData,
				fileUpload?.Loan_Number
			);
		} catch (err) {
			console.log("95 err:", err);
			setError(err?.data?.details);
			return false;
		}
	};

	const validateSection = useCallback(() => {
		return runSilentValidation([
			(setError) =>
				requiredFields(
					loanApplicationData,
					propertyInfo,
					grossRent,
					homeownersInsurance,
					propertyTaxes,
					loanOriginatorEmail,
					loanProcessorEmail,
					manualOriginator,
					originatorData,
					setError,
					firstTimeHomebuyerSelections.current
				),
			(setError) => {
				if (
					manualOriginator &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForManualLoanOriginator(originatorData, setError);
				}

				return true;
			},
			(setError) => {
				if (
					additionalContact?.name !== "" &&
					(selectedUserWithLoanChannel(
						userDetails,
						impersonatedUser,
						selectedOrganization
					)?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
						selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
				) {
					return requiredFieldsForAdditionalContact(additionalContact, setError);
				}

				return true;
			},
		]);
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	const highlightSectionErrors = useCallback(() => {
		setError("");

		if (
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError,
				firstTimeHomebuyerSelections.current
			)
		) {
			return;
		}

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return;
			}
		}

		if (
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return;
			}
		}
	}, [
		additionalContact,
		grossRent,
		homeownersInsurance,
		impersonatedUser,
		loanApplicationData,
		loanOriginatorEmail,
		loanProcessorEmail,
		manualOriginator,
		originatorData,
		propertyInfo,
		propertyTaxes,
		selectedOrganization,
		userDetails,
	]);

	useRegisterLoanSection(registerSectionHandler, {
		validate: validateSection,
		save: () => handleNext(false, true),
		highlightErrors: highlightSectionErrors,
	});

	const handleCreateCalyxLoan = async () => {
		console.log("940 application data:", applicationData);
		console.log("940 loan app data:", loanApplicationData);
		console.log("940 property data:", propertyInfo);

		setError("");

		if (
			!requiredFields(
				loanApplicationData,
				propertyInfo,
				grossRent,
				homeownersInsurance,
				propertyTaxes,
				loanOriginatorEmail,
				loanProcessorEmail,
				manualOriginator,
				originatorData,
				setError,
				firstTimeHomebuyerSelections.current
			)
		) {
			return;
		}

		if (
			manualOriginator &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForManualLoanOriginator(originatorData, setError)) {
				return;
			}
		}

		if (
			additionalContact?.name !== "" &&
			(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
		) {
			if (!requiredFieldsForAdditionalContact(additionalContact, setError)) {
				return;
			}
		}

		const resolvedLoLp = await ensureValidLoLpForSubmit();

		if (!resolvedLoLp) {
			return;
		}

		const { effectiveLoanOriginator, effectiveLoanProcessor } = resolvedLoLp;

		let updatedData;

		if (!applicationData.hasOwnProperty("property")) {
			console.log("'property' key not found in the application data object.");
		} else {
			// Update the properties object with values from obj2
			const updatedProperties = {
				...applicationData.property,
				...propertyInfo,
			};

			let updatedBorrowers = [];

			for (let index = 0; index < loanApplicationData.length; index++) {
				if (index % 2 === 0) {
					if (
						loanApplicationData[index]?.urla2020CitizenshipResidencyType ===
						"Foreign National"
					) {
						loanApplicationData[index].urla2020CitizenshipResidencyType =
							"NonPermanentResidentAlien";
					}

					if (
						loanApplicationData[index + 1]?.urla2020CitizenshipResidencyType ===
						"Foreign National"
					) {
						loanApplicationData[index + 1].urla2020CitizenshipResidencyType =
							"NonPermanentResidentAlien";
					}

					updatedBorrowers.push({
						borrower: loanApplicationData[index],
						coborrower: loanApplicationData[index + 1],
					});
				}
			}

			console.log("965 updated borrowers:", updatedBorrowers);

			// Update the monthly payment for the specified fee type
			let updatedFees = [];

			if (
				applicationData?.fees === undefined ||
				applicationData?.fees?.length === 0
			) {
				updatedFees.push({
					feeType: "FloodInsurance",
					monthlyPayment: floodInsurance,
				});

				updatedFees.push({
					feeType: "PropertyTaxes",
					monthlyPayment: propertyTaxes,
				});
			} else {
				applicationData?.fees?.map((fee) => {
					if (fee.feeType === "FloodInsurance") {
						updatedFees.push({ ...fee, monthlyPayment: floodInsurance });
					}

					if (fee.feeType === "PropertyTaxes") {
						updatedFees.push({ ...fee, monthlyPayment: propertyTaxes });
					}

					return fee;
				});
			}
			console.log("1070 updated fees:", updatedFees);

			let tpoData = {};

			if (
				manualOriginator &&
				(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
					selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
			) {
				tpoData["companyName"] = organizationDetails?.name;
				tpoData["companyId"] = organizationDetails?.company_id;
				tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
				tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
				tpoData["companyAddress"] = organizationDetails?.address;
				tpoData["companyCity"] = organizationDetails?.city;
				tpoData["companyState"] = organizationDetails?.state;
				tpoData["companyZip"] = organizationDetails?.zip;
				tpoData["companyPhone"] = organizationDetails?.phone;
				tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
				tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
			} else if (
				selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
			) {
				tpoData["companyName"] = organizationDetails?.name;
				tpoData["companyId"] = organizationDetails?.company_id;
				tpoData["companyOrganizationId"] = organizationDetails?.organization_id;
				tpoData["companyLegalName"] = organizationDetails?.company_legal_name;
				tpoData["companyAddress"] = organizationDetails?.address;
				tpoData["companyCity"] = organizationDetails?.city;
				tpoData["companyState"] = organizationDetails?.state;
				tpoData["companyZip"] = organizationDetails?.zip;
				tpoData["companyPhone"] = organizationDetails?.phone;
				tpoData["companyAeName"] = organizationDetails?.sales_rep_name;
				tpoData["companyAeUserName"] = organizationDetails?.sales_rep_id;
			} else {
				tpoData["companyName"] = effectiveLoanOriginator?.organization?.name;
				tpoData["companyId"] = effectiveLoanOriginator?.organization?.company_id;
				tpoData["companyOrganizationId"] =
					effectiveLoanOriginator?.organization?.organization_id;
				tpoData["companyLegalName"] =
					effectiveLoanOriginator?.organization?.company_legal_name;
				tpoData["companyAddress"] = effectiveLoanOriginator?.organization?.address;
				tpoData["companyCity"] = effectiveLoanOriginator?.organization?.city;
				tpoData["companyState"] = effectiveLoanOriginator?.organization?.state;
				tpoData["companyZip"] = effectiveLoanOriginator?.organization?.zip;
				tpoData["companyPhone"] = effectiveLoanOriginator?.organization?.phone;
				tpoData["companyAeName"] = effectiveLoanOriginator?.organization?.sales_rep_name;
				tpoData["companyAeUserName"] =
					effectiveLoanOriginator?.organization?.sales_rep_id;
			}

			if (
				childOrganizationDetails ||
				selectedUser(userDetails, impersonatedUser)?.child_org_name !== ""
			) {
				tpoData["branchName"] = childOrganizationDetails?.name;
				tpoData["branchId"] = childOrganizationDetails?.company_id;
				tpoData["branchOrganizationId"] =
					childOrganizationDetails?.organization_id;
				tpoData["branchLegalName"] =
					childOrganizationDetails?.company_legal_name;
				tpoData["branchAddress"] = childOrganizationDetails?.address;
				tpoData["branchCity"] = childOrganizationDetails?.city;
				tpoData["branchState"] = childOrganizationDetails?.state;
				tpoData["branchZip"] = childOrganizationDetails?.zip;
				tpoData["branchPhone"] = childOrganizationDetails?.phone;
				tpoData["branchAeName"] = childOrganizationDetails?.sales_rep_name;
				tpoData["branchAeUserName"] = childOrganizationDetails?.sales_rep_id;
			}

			if (
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
			) {
				tpoData["loEmail"] = additionalContact?.email;
				tpoData["loName"] = additionalContact?.name;
				tpoData["loBusinessPhone"] = additionalContact?.phone;
			} else {
				tpoData["loEmail"] = effectiveLoanOriginator?.user?.email;
				tpoData["loStatus"] = effectiveLoanOriginator?.login_enabled;
				tpoData["loAddress"] = effectiveLoanOriginator?.address;
				tpoData["loCity"] = effectiveLoanOriginator?.city;
				tpoData["loState"] = effectiveLoanOriginator?.state;
				tpoData["loZip"] = effectiveLoanOriginator?.zip_code;
				tpoData["loName"] = buildLoLpFullName(effectiveLoanOriginator);
				tpoData["loBusinessPhone"] = effectiveLoanOriginator?.phone;
			}

			console.log("1120 loanProcessor email:", loanProcessorEmail);
			console.log("1120 loanProcessor:", effectiveLoanProcessor);

			tpoData["lpEmail"] = effectiveLoanProcessor?.user?.email;
			tpoData["lpStatus"] = effectiveLoanProcessor?.login_enabled;
			tpoData["lpAddress"] = effectiveLoanProcessor?.address;
			tpoData["lpCity"] = effectiveLoanProcessor?.city;
			tpoData["lpState"] = effectiveLoanProcessor?.state;
			tpoData["lpZip"] = effectiveLoanProcessor?.zip_code;
			tpoData["lpName"] = buildLoLpFullName(effectiveLoanProcessor);
			tpoData["lpBusinessPhone"] = effectiveLoanProcessor?.phone;

			console.log("930 tpoData:", tpoData);

			let originatorInfoData = {};

			if (
				manualOriginator &&
				(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
					selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
						?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR)
			) {
				originatorInfoData["originatorAddressLineText"] =
					originatorData?.address;
				originatorInfoData["originatorAddressUnitIdentifier"] =
					originatorData?.unitNumber;
				originatorInfoData["interviewersCompanyStateLicense"] =
					originatorData?.orgStateLicense;
				originatorInfoData["interviewerEmail"] =
					originatorData?.originatorEmail;
				originatorInfoData["originatorFirstName"] =
					originatorData?.originatorFirstName;
				originatorInfoData["originatorLastName"] =
					originatorData?.originatorLastName;
				originatorInfoData["nmlsLoanOriginatorId"] =
					originatorData?.originatorNmls;
				originatorInfoData["interviewerLicenseIdentifier"] =
					originatorData?.originatorStateLicense;
				originatorInfoData["interviewerPhoneNumber"] =
					originatorData?.originatorPhone;
			} else {
				originatorInfoData["originatorAddressLineText"] =
					effectiveLoanOriginator?.organization?.address;
				originatorInfoData["interviewerEmail"] =
					effectiveLoanOriginator?.user?.email;
				originatorInfoData["interviewerName"] = buildLoLpFullName(
					effectiveLoanOriginator
				);
				originatorInfoData["originatorFirstName"] =
					effectiveLoanOriginator?.user?.first_name;
				originatorInfoData["originatorLastName"] =
					effectiveLoanOriginator?.user?.last_name;
				originatorInfoData["nmlsLoanOriginatorId"] =
					effectiveLoanOriginator?.nmls_id;

				originatorInfoData["interviewerPhoneNumber"] =
					effectiveLoanOriginator?.phone;
				originatorInfoData["interviewerLicenseIdentifier"] = "";
			}

			console.log("980 originatorInfoData:", originatorInfoData);

			// Create a new data object with the updated properties
			updatedData = {
				...applicationData,
				subjectPropertyGrossRentalIncomeAmount: grossRent,
				proposedHazardInsuranceAmount: homeownersInsurance,
				proposedDuesAmount: hoaDues,
				fees: updatedFees,
				proposedRealEstateTaxesAmount:
					updatedFees?.find((item) => item?.feeType === "PropertyTaxes")
						?.monthlyPayment || "0",
				applications: updatedBorrowers,
				property: updatedProperties,
				tpo: {
					loAeName: effectiveLoanOriginator?.organization?.sales_rep_id,
					...tpoData,
				},
				...originatorInfoData,
			};
		}

		console.log("975 updated data:", updatedData);

		console.log("1000 lo email:", effectiveLoanOriginator?.user?.email);
		console.log("1000 lp email:", effectiveLoanProcessor?.user?.email);

		let userEmail;

		if (
			impersonatedUser?.email !== null &&
			impersonatedUser?.email !== undefined &&
			impersonatedUser?.email !== ""
		) {
			userEmail = impersonatedUser?.email;
		} else if (
			loanOriginatorEmail !== undefined &&
			loanOriginatorEmail !== ""
		) {
			userEmail = loanOriginatorEmail;
		} else {
			userEmail = userDetails?.email;
		}

		// Step A: Fetch AE Emails — must succeed before loan creation
		let aeEmailData = null;
		try {
			setAeEmailErrors(null);
			const aeResult = await fetchAEEmails({
				broker_id: userDetails?.organizations?.find(org => org.name === selectedOrganization)?.tpo_broker_id || userDetails?.user_tpo_id || impersonatedUser?.user_tpo_id,
				loan_processor_broker_id: loanOriginatorId,
			}).unwrap();
			const hasAEEmail =
				aeResult?.ae_email ||
				aeResult?.branch_ae_email ||
				aeResult?.company_ae_email;
			if (!hasAEEmail) {
				handleOpenCommonErrorModal("AE_EMAIL_NOT_FOUND");
				return;
			}
			if (aeResult?.errors) {
				setAeEmailErrors(aeResult.errors);
				console.warn("[AE Email] Partial fetch errors:", aeResult.errors);
			}
			aeEmailData = aeResult;
		} catch (err) {
			console.error("[AE Email] Fetch failed:", err);
			setError(err?.data?.message || "AE email lookup failed. Please try again.");
			return;
		}

		// Step B: Create Calyx Loan
		try {
			const fileUpload = await createCalyxLoan(
				withDirectPathPayload(
					{
						email: userEmail,
						data: updatedData,
						loan_channel: selectedUserWithLoanChannel(
							userDetails,
							impersonatedUser,
							selectedOrganization
						)?.loan_channel,
						selectedOrganization: effectiveOrgName,
						userDetails,
					},
					directPath
				)
			).unwrap();
			console.log("90 create loan:", fileUpload);
			dispatch(setLoEmail(effectiveLoanOriginator?.user?.email || ""));
			dispatch(setLpEmail(effectiveLoanProcessor?.user?.email || ""));
			setLoanGuid(fileUpload?.GUID);
			updateField(
				fileUpload?.GUID,
				updatedData,
				true,
				aeEmailData,
				fileUpload?.Loan_Number
			);
		} catch (err) {
			console.log("975 err:", err);
			setError(err?.data?.errors[0]?.summary);
		}
	};

	const handleAddBorrowerPair = () => {
		setLoanApplicationData([
			...loanApplicationData,
			{ mailingAddressSameAsPresentIndicator: false },
			{ mailingAddressSameAsPresentIndicator: false },
		]);
	};

	const handleRemoveBorrowerPair = () => {
		console.log("1000 tab value:", tabValue);
		console.log("1000 pair count:", loanApplicationData?.length / 2 - 1);

		if (tabValue === loanApplicationData?.length / 2 - 1) {
			setTabValue(tabValue - 1);
		}

		let newArr = loanApplicationData;

		// remove current selected borrower pair with following logic
		// tabvalue ~ loanApplicationData
		// 0 ~ 0, 1
		// 1 ~ 2, 3
		// 2 ~ 4, 5

		if (tabValue === 0) {
			newArr?.splice(tabValue, 2);
		} else {
			newArr?.splice(tabValue * 2, 2);
		}

		setLoanApplicationData([...newArr]);
	};

	function a11yProps(index) {
		return {
			id: `simple-tab-${index}`,
			"aria-controls": `simple-tabpanel-${index}`,
		};
	}

	const handleChangeTab = (event, newValue) => {
		setTabValue(newValue);
	};

	return (
		<Stack direction="column" className={styles.stackContainer}>
			<Stack className={styles.bodyContainer}>
				{/* tabs section */}
				<Tabs
					value={tabValue}
					onChange={handleChangeTab}
					aria-label="basic tabs example"
					TabIndicatorProps={{
						style: {
							backgroundColor: "#49787F",
						},
					}}
				>
					<Tab
						className={styles.tabBtnContainer}
						label={
							<Stack direction="row" className={styles.tabLabelContainer}>
								<Heading6
									text="Borrower Pair 1"
									fontType="semibold"
									extraClass={
										tabValue === 0 ? styles.activeTabText : styles.tabText
									}
								/>

								{tabValue === 0 && (
									<Stack
										className={styles.closeIconContainer}
										onClick={() =>
											loanApplicationData?.length > 2 &&
											handleRemoveBorrowerPair()
										}
									>
										<CloseOutlined className={styles.closeIcon} />
									</Stack>
								)}
							</Stack>
						}
						{...a11yProps(0)}
					/>
					{loanApplicationData?.length > 2 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 2"
										fontType="semibold"
										extraClass={
											tabValue === 1 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 1 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(1)}
						/>
					)}
					{loanApplicationData?.length > 4 && (
						<Tab
							className={styles.tabBtnContainer}
							label={
								<Stack direction="row" className={styles.tabLabelContainer}>
									<Heading6
										text="Borrower Pair 3"
										fontType="semibold"
										extraClass={
											tabValue === 2 ? styles.activeTabText : styles.tabText
										}
									/>

									{tabValue === 2 && (
										<Stack
											className={styles.closeIconContainer}
											onClick={() => handleRemoveBorrowerPair()}
										>
											<CloseOutlined className={styles.closeIcon} />
										</Stack>
									)}
								</Stack>
							}
							{...a11yProps(2)}
						/>
					)}

					<Box>
						<IconButton
							className={styles.addPairContainer}
							onClick={() =>
								loanApplicationData?.length < 6 && handleAddBorrowerPair()
							}
							disabled={loanApplicationData?.length > 4}
						>
							<AddOutlined className={styles.addPairIcon} />
						</IconButton>
					</Box>
				</Tabs>

				{/* borrower pair 1 */}
				{loanApplicationData?.length > 0 && (
					<TabPanel
						value={tabValue}
						index={0}
						className={tabValue === 0 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(0, 2)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error?.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}

				{/* borrower pair 2 */}
				{loanApplicationData?.length > 2 && (
					<TabPanel
						value={tabValue}
						index={1}
						className={tabValue === 1 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(2, 4)?.map((borrower, index) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 2}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}

				{/* borrower pair 3 */}
				{loanApplicationData?.length > 4 && (
					<TabPanel
						value={tabValue}
						index={2}
						className={tabValue === 2 ? styles.tabPanelContainer : ""}
					>
						{loanApplicationData?.slice(4, 6)?.map((borrower, index = 4) => (
							<BorrowerInformation
								key={index}
								borrowerIndex={index + 4}
								coBorrower={index % 2 !== 0}
								borrowerData={borrower}
								loanGuid={loanGuid}
								setFirstTimeHomebuyerSelection={setFirstTimeHomebuyerSelection}
								foreignNationalIndex={foreignNationalIndex}
								setForeignNationalIndex={setForeignNationalIndex}
								data={loanApplicationData}
								setData={setLoanApplicationData}
								error={error.includes("provide a value")}
							/>
						))}

						<PropertyDetails
							data={propertyInfo}
							setData={setPropertyInfo}
							loanData={applicationData}
							setLoanData={setApplicationData}
							error={error.includes("provide a value")}
							grossRent={grossRent}
							setGrossRent={setGrossRent}
							homeownersInsurance={homeownersInsurance}
							setHomeownersInsurance={setHomeownersInsurance}
							floodInsurance={floodInsurance}
							setFloodInsurance={setFloodInsurance}
							propertyTaxes={propertyTaxes}
							setPropertyTaxes={setPropertyTaxes}
							realEstateTaxes={realEstateTaxes}
							setRealEstateTaxes={setRealEstateTaxes}
							hoaDues={hoaDues}
							setHoaDues={setHoaDues}
							setHelpText={setHelpText}
							handleOpenHelpText={handleOpenHelpText}
						/>

						<LoanOriginator
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanOriginatorEmail={loanOriginatorEmail}
							setLoanOriginatorEmail={setLoanOriginatorEmail}
							loanOriginatorId={loanOriginatorId}
							setLoanOriginatorId={setLoanOriginatorId}
							organizationUsers={organizationUsers}
							loanOriginator={loanOriginator}
							setLoanOriginator={setLoanOriginator}
							setOrganizationLicense={setOrganizationLicense}
							propertyState={propertyInfo?.state}
							manualOriginator={manualOriginator}
							setManualOriginator={setManualOriginator}
							originatorData={originatorData}
							setOriginatorData={setOriginatorData}
							setHelpText={setHelpText}
							setExtraHelpText={setExtraHelpText}
							handleOpenHelpText={handleOpenHelpText}
							error={error.includes("provide a value")}
							allowManualInput={allowManualInput}
						/>

						<LoanProcessor
							organizationName={currentOrgName}
							organizationDetails={currentOrgDetails}
							loanProcessorEmail={loanProcessorEmail}
							setLoanProcessorEmail={setLoanProcessorEmail}
							loanProcessorId={loanProcessorId}
							setLoanProcessorId={setLoanProcessorId}
							organizationProcessor={organizationProcessor}
							loanProcessor={loanProcessor}
							propertyState={propertyInfo?.state}
							error={error.includes("provide a value")}
						/>

						{(selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
							?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
							selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
								?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR) && (
								<AdditionalLoanContact
									additionalContact={additionalContact}
									setAdditionalContact={setAdditionalContact}
									error={error.includes("provide a value")}
								/>
							)}
					</TabPanel>
				)}
			</Stack>

			{/* loan flow error */}
			{error !== "" && (
				<RequiredError
					headerText={
						error.includes("provide a value") || error.includes("borrower.dependentCount")
							? "Missing fields"
							: "Error"
					}
					text={
						error.includes("Something went wrong")
							? "Updates Not Received by LOS. Please Try Again."
							: error.includes("provide a value")
								? "Please provide a value for all fields highlighted above."
								: error.includes("borrower.dependentCount")
									? "Please check your dependent count."
									: error
					}
				/>
			)}

			{aeEmailErrors && (
				<p style={{ color: "#f59e0b", fontSize: 12, marginTop: 4 }}>
					AE email lookup partially failed. Loan was created successfully.
				</p>
			)}

			<Stack direction="row" className={styles.footerContainer}>
				<PrimaryButton
					text="Save and Continue"
					endIcon={<ArrowForwardOutlined className={styles.nextIcon} />}
					onClick={() => {
						xmlType === "CALYX MISMO" ? handleCreateCalyxLoan() : handleNext();
					}}
				/>
			</Stack>
			{/* modal */}
			<LoaderModal
				open={
					isFetchingAEEmails ||
					creatingCalyxLoan ||
					creatingLoan ||
					creatingAdminLoan ||
					isUpdatingFields ||
					creatingBorrowerPair
				}
			/>
			<HelpTextModal
				open={helpTextOpen}
				modalText={helpText}
				extraHelpText={extraHelpText}
				handleClose={handleCloseHelpText}
			/>
			<CommanErrorModal
				isOpen={commonErrorModalVisible}
				onClose={handleCloseCommonErrorModal}
				title={commonErrorData?.title}
				message={commonErrorData?.message}
				errorCode={commonErrorData?.code}
				errorResponse={commonErrorData?.error_response}
			/>
		</Stack>
	);
};

export default LoanApplicationForm;
